<template>
  <div class="manual-order rtl pa-3 pa-md-6" v-if="isManager">
    <div class="page-head d-flex align-center mb-2">
      <v-avatar color="secondary" size="48" class="ml-3 flex-shrink-0">
        <v-icon color="white">fa-file-invoice</v-icon>
      </v-avatar>
      <div>
        <h1 class="text-h5 font-weight-black mb-1">ثبت سفارش دستی</h1>
        <p class="grey--text text--darken-1 mb-0 text-body-2">
          اطلاعات مشتری، اقلام سفارش و هزینه ارسال را با دقت ثبت کنید.
        </p>
      </div>
    </div>

    <v-breadcrumbs :items="breadcrumbItems" class="px-0 pt-0 pb-4">
      <template v-slot:divider>
        <v-icon small>fa-chevron-left</v-icon>
      </template>
    </v-breadcrumbs>

    <v-row>
      <v-col cols="12" md="8" lg="9">
        <v-row>
          <v-col cols="12" md="6">
            <v-card flat class="order-card rounded-xl pa-4 pa-md-5 mb-5 fill-height">
              <div class="card-title text-subtitle-1 font-weight-bold mb-3">
                <v-icon small color="secondary" class="ml-2">fa-user</v-icon>
                مشتری
              </div>

              <v-btn-toggle v-model="customerMode" mandatory dense class="mb-4">
                <v-btn value="existing" small>مشتری قبلی</v-btn>
                <v-btn value="new" small>مشتری جدید</v-btn>
              </v-btn-toggle>

              <customer-select
                  v-if="customerMode === 'existing'"
                  v-model="selectedCustomer"
                  placeholder="جستجو و انتخاب مشتری"
              ></customer-select>

              <v-row v-else>
                <v-col cols="12" sm="4">
                  <v-text-field v-model="newCustomer.first_name" outlined dense hide-details label="نام"></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field v-model="newCustomer.last_name" outlined dense hide-details label="نام خانوادگی"></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field v-model="newCustomer.mobile_number" outlined dense hide-details label="شماره موبایل *"></v-text-field>
                </v-col>
              </v-row>
            </v-card>
          </v-col>

          <v-col cols="12" md="6">
            <v-card flat class="order-card rounded-xl pa-4 pa-md-5 mb-5 fill-height">
              <div class="card-title text-subtitle-1 font-weight-bold mb-3">
                <v-icon small color="secondary" class="ml-2">fa-map-marker-alt</v-icon>
                آدرس ارسال
              </div>
              <v-row>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="address.first_name" outlined dense hide-details label="نام گیرنده"></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="address.last_name" outlined dense hide-details label="نام خانوادگی گیرنده"></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="address.state" outlined dense hide-details label="استان *"></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="address.city" outlined dense hide-details label="شهر *"></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="address.address" outlined dense hide-details label="آدرس کامل *"></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field v-model="address.house_number" outlined dense hide-details label="پلاک"></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field v-model="address.house_unit" outlined dense hide-details label="واحد"></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field v-model="address.zip_code" outlined dense hide-details label="کد پستی"></v-text-field>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <v-card flat class="order-card rounded-xl pa-4 pa-md-5 mb-5">
          <div class="card-title text-subtitle-1 font-weight-bold mb-3">
            <v-icon small color="secondary" class="ml-2">fa-shopping-bag</v-icon>
            اقلام سفارش
          </div>

          <product-select
              v-model="pendingProductId"
              placeholder="افزودن محصول به سبد"
              @select="addProductToCart"
          ></product-select>

          <v-simple-table class="mt-4" v-if="cartItems.length">
            <thead>
            <tr>
              <th>محصول</th>
              <th style="width:140px;">قیمت واحد</th>
              <th style="width:130px;">تخفیف٪</th>
              <th style="width:130px;">تعداد</th>
              <th style="width:140px;">قیمت کل</th>
              <th style="width:60px;"></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(line, index) in cartItems" :key="line.product.id">
              <td>{{ line.product.name }}</td>
              <td>
                <v-text-field
                    :value="line.unit_price"
                    type="number"
                    dense
                    outlined
                    hide-details
                    min="0"
                    suffix="تومان"
                    @input="applyUnitPrice(line, $event)"
                ></v-text-field>
              </td>
              <td>
                <v-text-field
                    :value="line.discount_percent"
                    type="number"
                    dense
                    outlined
                    hide-details
                    min="0"
                    max="100"
                    suffix="%"
                    @input="applyDiscountPercent(line, $event)"
                ></v-text-field>
                <v-btn
                    v-if="line.discount_percent"
                    x-small
                    text
                    color="grey"
                    class="pa-0 mt-1"
                    @click="resetLinePrice(line)"
                >بازگشت به قیمت اصلی</v-btn>
              </td>
              <td>
                <v-text-field
                    v-model.number="line.quantity"
                    type="number"
                    dense
                    outlined
                    hide-details
                    :min="1"
                    :max="stockOf(line.product)"
                    @input="clampQuantity(line)"
                ></v-text-field>
                <div class="text-caption grey--text mt-1" v-if="stockOf(line.product) != null">
                  موجودی: {{ stockOf(line.product) }}
                </div>
              </td>
              <td>{{ formatPrice(line.unit_price * line.quantity) }}</td>
              <td>
                <v-btn icon small color="error" @click="removeLine(index)">
                  <v-icon small>fa-trash</v-icon>
                </v-btn>
              </td>
            </tr>
            </tbody>
          </v-simple-table>

          <div v-else class="empty-items text-center grey--text py-8">
            <v-icon size="34" color="grey lighten-1" class="mb-2 d-block mx-auto">fa-box-open</v-icon>
            هنوز محصولی اضافه نشده است
          </div>
        </v-card>

        <v-card flat class="order-card rounded-xl pa-4 pa-md-5 mb-5">
          <div class="card-title text-subtitle-1 font-weight-bold mb-3">
            <v-icon small color="secondary" class="ml-2">fa-truck</v-icon>
            هزینه ارسال
          </div>
          <div class="text-caption grey--text mb-2">روش ارسال</div>
          <v-btn-toggle v-model="shippingMode" mandatory dense class="mb-3">
            <v-btn value="standard" small>استاندارد</v-btn>
            <v-btn value="free" small>رایگان</v-btn>
            <v-btn value="manual" small>دستی</v-btn>
          </v-btn-toggle>
          <v-text-field
              v-if="shippingMode === 'manual'"
              v-model="shippingCost"
              type="number"
              outlined
              dense
              hide-details
              label="هزینه ارسال (تومان) *"
              class="shipping-cost-input"
              suffix="تومان"
          ></v-text-field>
          <div class="text-caption grey--text mt-2" v-else-if="shippingMode === 'standard'">
            طبق تعرفه پیش‌فرض سامانه محاسبه می‌شود
          </div>
        </v-card>

        <v-card flat class="order-card rounded-xl pa-4 pa-md-5 mb-5">
          <div class="card-title text-subtitle-1 font-weight-bold mb-3">
            <v-icon small color="secondary" class="ml-2">fa-tasks</v-icon>
            وضعیت نهایی سفارش
          </div>
          <v-radio-group v-model="targetStatus" row hide-details class="mt-0">
            <v-radio label="جاری" value="paid"></v-radio>
            <v-radio label="در حال بسته‌بندی" value="processing"></v-radio>
            <v-radio label="ارسال شده" value="shipped"></v-radio>
          </v-radio-group>
          <v-checkbox
              v-model="sendSms"
              label="پیامک ثبت سفارش برای مشتری ارسال شود"
              hide-details
              class="mt-3"
          ></v-checkbox>
        </v-card>
      </v-col>

      <v-col cols="12" md="4" lg="3">
        <div class="summary-sticky">
          <v-card flat class="order-card summary-card rounded-xl pa-4 pa-md-5">
            <div class="card-title text-subtitle-1 font-weight-bold mb-4">
              <v-icon small color="secondary" class="ml-2">fa-clipboard-list</v-icon>
              خلاصه سفارش
            </div>

            <div class="summary-row d-flex justify-space-between align-center mb-2">
              <span class="grey--text text--darken-1 text-body-2">تعداد اقلام</span>
              <span class="font-weight-medium">{{ totalQuantity }}</span>
            </div>
            <div class="summary-row d-flex justify-space-between align-center mb-2">
              <span class="grey--text text--darken-1 text-body-2">مجموع قیمت کالاها</span>
              <span class="font-weight-medium">{{ formatPrice(orderTotal) }} تومان</span>
            </div>
            <div class="summary-row d-flex justify-space-between align-center mb-2">
              <span class="grey--text text--darken-1 text-body-2">هزینه ارسال</span>
              <span class="font-weight-medium">{{ shippingDisplayText }}</span>
            </div>

            <v-divider class="my-3"></v-divider>

            <div class="d-flex justify-space-between align-center mb-4">
              <span class="text-subtitle-2 font-weight-bold">مبلغ قابل پرداخت</span>
              <span class="text-h6 font-weight-black secondary--text">{{ formatPrice(grandTotalDisplay) }} تومان</span>
            </div>

            <v-btn
                block
                color="secondary"
                depressed
                large
                :loading="submitting"
                :disabled="!canSubmit"
                @click="submitOrder"
            >
              <v-icon small class="ml-2">fa-save</v-icon>
              ثبت سفارش
            </v-btn>
          </v-card>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import ProductSelect from "@/components/selects/ProductSelect";
import CustomerSelect from "@/components/selects/CustomerSelect";

export default {
  name: "ManualOrderCreate",
  components: { ProductSelect, CustomerSelect },
  data() {
    return {
      breadcrumbItems: [
        { text: "داشبورد", to: { name: "Home" }, exact: true },
        { text: "سفارش‌ها", disabled: true },
        { text: "ثبت سفارش دستی", disabled: true },
      ],
      pendingProductId: null,
      cartItems: [],
      customerMode: "existing",
      selectedCustomer: null,
      newCustomer: { first_name: "", last_name: "", mobile_number: "" },
      address: {
        first_name: "", last_name: "", state: "", city: "",
        address: "", house_number: "", house_unit: "", zip_code: "",
      },
      targetStatus: "processing",
      sendSms: true,
      shippingMode: "standard",
      shippingCost: null,
      submitting: false,
    };
  },
  computed: {
    isManager() {
      return !!(this.user && (this.user.user_type === "ma" || this.user.is_superuser));
    },
    orderTotal() {
      return this.cartItems.reduce((sum, line) => sum + (line.unit_price * line.quantity), 0);
    },
    totalQuantity() {
      return this.cartItems.reduce((sum, line) => sum + (line.quantity || 0), 0);
    },
    knownShippingCost() {
      if (this.shippingMode === "free") return 0;
      if (this.shippingMode === "manual") return Number(this.shippingCost) || 0;
      return null;
    },
    shippingDisplayText() {
      if (this.knownShippingCost == null) return "طبق تعرفه پیش‌فرض";
      return `${this.formatPrice(this.knownShippingCost)} تومان`;
    },
    grandTotalDisplay() {
      return this.orderTotal + (this.knownShippingCost || 0);
    },
    canSubmit() {
      if (this.submitting || !this.cartItems.length) return false;
      if (this.customerMode === "existing") {
        if (!this.selectedCustomer) return false;
      } else if (!(this.newCustomer.mobile_number || "").trim()) {
        return false;
      }
      if (!(this.address.state && this.address.city && this.address.address)) return false;
      if (this.shippingMode === "manual" && (this.shippingCost === null || this.shippingCost === "")) return false;
      return true;
    },
  },
  created() {
    this.initializeForUser(this.user);
  },
  mounted() {
    this.setDocumentTitle("ثبت سفارش دستی");
  },
  watch: {
    user(value) {
      this.initializeForUser(value);
    },
    selectedCustomer(customer) {
      if (customer) this.fillAddressFromCustomer(customer);
    },
  },
  methods: {
    initializeForUser(value) {
      if (!value) return;
      if (!this.isManager) {
        this.notify("این بخش فقط برای مدیر کل قابل دسترسی است", "danger");
        this.$router.replace({ name: "Home" });
      }
    },
    stockOf(product) {
      return product && product.inventory_count != null ? product.inventory_count : null;
    },
    addProductToCart(product) {
      const existing = this.cartItems.find((line) => String(line.product.id) === String(product.id));
      if (existing) {
        this.clampQuantity(existing, existing.quantity + 1);
      } else {
        this.cartItems.push({ product, quantity: 1, unit_price: product.price, discount_percent: 0 });
      }
      this.$nextTick(() => { this.pendingProductId = null; });
    },
    clampQuantity(line, nextQuantity) {
      let quantity = nextQuantity != null ? nextQuantity : line.quantity;
      quantity = Math.max(1, Math.floor(Number(quantity) || 1));
      const stock = this.stockOf(line.product);
      if (stock != null && stock > 0 && quantity > stock) {
        quantity = stock;
        this.notify("بیشتر از موجودی انبار نمی‌توان ثبت کرد", "warning");
      }
      line.quantity = quantity;
    },
    applyDiscountPercent(line, rawValue) {
      let pct = Number(rawValue);
      if (!Number.isFinite(pct)) pct = 0;
      pct = Math.min(100, Math.max(0, pct));
      line.discount_percent = pct;
      line.unit_price = Math.round(line.product.price * (1 - pct / 100));
    },
    applyUnitPrice(line, rawValue) {
      let price = Number(rawValue);
      if (!Number.isFinite(price)) price = 0;
      // قیمت واحد نمی‌تونه از قیمت اصلی محصول بیشتر بشه، چون بک‌اند تخفیف منفی
      // (markup) رو رد می‌کنه — این ویرایش فقط برای تخفیف دادنه، نه افزایش قیمت.
      price = Math.min(line.product.price, Math.max(0, Math.round(price)));
      line.unit_price = price;
      line.discount_percent = line.product.price
          ? Math.round((1 - price / line.product.price) * 10000) / 100
          : 0;
    },
    resetLinePrice(line) {
      line.unit_price = line.product.price;
      line.discount_percent = 0;
    },
    removeLine(index) {
      this.cartItems.splice(index, 1);
    },
    fillAddressFromCustomer(customer) {
      this.request({
        url: this.endpoint(`shop/adminCustomerAddresses/${customer.id}`),
        method: "get",
        success: (data) => {
          const list = Array.isArray(data) ? data : (data.results || []);
          const found = list[0];
          if (!found) return;
          this.address = {
            first_name: found.first_name || "",
            last_name: found.last_name || "",
            state: found.state || "",
            city: found.city || "",
            address: found.address || "",
            house_number: found.house_number || "",
            house_unit: found.house_unit || "",
            zip_code: found.zip_code || "",
          };
        },
      });
    },
    formatPrice(value) {
      return new Intl.NumberFormat("fa-IR").format(value || 0);
    },
    buildPayload() {
      const payload = {
        items: this.cartItems.map((line) => ({
          product_id: line.product.id,
          quantity: line.quantity,
          discount_type: "percent",
          discount_value: line.discount_percent,
        })),
        address: { ...this.address },
        status: this.targetStatus,
        send_sms: this.sendSms,
        shipping_mode: this.shippingMode,
        shipping_cost: this.shippingMode === "manual" ? this.shippingCost : null,
      };
      if (this.customerMode === "existing") {
        payload.customer_id = this.selectedCustomer.id;
      } else {
        payload.customer = { ...this.newCustomer };
      }
      return payload;
    },
    submitOrder() {
      if (!this.canSubmit) return;
      this.submitting = true;
      this.request({
        url: this.endpoint("shop/adminManualOrder"),
        method: "post",
        data: this.buildPayload(),
        success: (data) => {
          this.submitting = false;
          this.notify(`سفارش با کد پیگیری ${data.order.exuni_tracking_code} ثبت شد`, "success");
          this.$router.push({ name: "AdminOrderDetail", params: { id: data.order.id } });
        },
        error: (err) => {
          this.submitting = false;
          const data = err && err.response && err.response.data;
          const message = (data && (data.error || data.detail)) || "ثبت سفارش با خطا مواجه شد";
          this.notify(typeof message === "string" ? message : JSON.stringify(message), "danger");
        },
      });
    },
  },
};
</script>

<style scoped>
.manual-order {
  max-width: 1400px;
  margin: 0 auto;
}
.order-card {
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.04) !important;
}
.card-title {
  display: flex;
  align-items: center;
}
.empty-items {
  border: 1px dashed rgba(0, 0, 0, 0.12);
  border-radius: 12px;
}
.summary-sticky {
  position: sticky;
  top: 24px;
}
.summary-card .summary-row span:last-child {
  white-space: nowrap;
}
</style>
